import { API_URL } from '@/utils/constants'

/**
 * "Your bitcoin is in one big piece": the wallet page's nudge to split a
 * large UTXO. Every purchase spends whole UTXOs, and a spent UTXO's change
 * cannot fund the next purchase until it confirms, so one big UTXO means one
 * purchase per block. Several mid-size ones mean several purchases at once.
 *
 * The API (`/site/split-prompt`) records what each wallet did so the business
 * dashboard can show the funnel and the split fees it earned. The prompt fires for a
 * UTXO worth $100 or more; the API's `THRESHOLD_SATS` mirrors the fallback.
 */
export const SPLIT_PROMPT_THRESHOLD_USD = 100
/** Used when no BTC price is available yet (about $100 at $84k). */
export const SPLIT_PROMPT_THRESHOLD_SATS = 120_000

/** Sats worth `SPLIT_PROMPT_THRESHOLD_USD` at this BTC/USD rate. */
export function thresholdSats(btcUsd: number | null | undefined): number {
	return btcUsd && Number.isFinite(btcUsd) && btcUsd > 1000
		? Math.ceil((SPLIT_PROMPT_THRESHOLD_USD / btcUsd) * 1e8)
		: SPLIT_PROMPT_THRESHOLD_SATS
}
/** UTXOs at least this big can fund a purchase on their own. */
export const USABLE_UTXO_SATS = 10_000
/** At this many usable UTXOs the wallet already has room to buy in parallel. */
export const ENOUGH_USABLE_UTXOS = 4
export const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000
/** Feature flag that turns the prompt on for everyone; admins always see it. */
export const SPLIT_PROMPT_FLAG = 'wallet::split_prompt'

/**
 * Wallets (taproot address) that get the prompt on every visit, whatever the
 * UTXO size, like the ?split_prompt=1 test link. For people testing the flow.
 */
export const SPLIT_PROMPT_TESTERS: ReadonlySet<string> = new Set([
	'bc1p57msm5hvxfhsf0hqers4vg5nr42ytpx2s4j8kg57dz9awqvmez9sj92gc3',
	'bc1p38khw2e6pl8324n9styk6ch2u4spu4a893azt6l5fhywmspeqzjsvgjmfm'
])

export function isSplitPromptTester(taprootAddress: string | null | undefined): boolean {
	return !!taprootAddress && SPLIT_PROMPT_TESTERS.has(taprootAddress.toLowerCase())
}

export type SplitPromptKind = 'measured' | 'shown' | 'clicked' | 'dismissed' | 'split_broadcast'

export interface UtxoShape {
	largestSats: number
	totalSats: number
	utxoCount: number
	usableCount: number
}

export function measureUtxos(utxos: { value?: number }[] | null | undefined): UtxoShape {
	const values = (utxos || [])
		.map((u) => Number(u?.value))
		.filter((v) => Number.isFinite(v) && v > 0)
	return {
		largestSats: values.reduce((max, v) => (v > max ? v : max), 0),
		totalSats: values.reduce((sum, v) => sum + v, 0),
		utxoCount: values.length,
		usableCount: values.filter((v) => v >= USABLE_UTXO_SATS).length
	}
}

/** One big piece, and not already spread over enough usable UTXOs. */
export function shouldPromptSplit(shape: UtxoShape, minSats = SPLIT_PROMPT_THRESHOLD_SATS): boolean {
	return shape.largestSats >= minSats && shape.usableCount < ENOUGH_USABLE_UTXOS
}

const SOURCE_KEY = 'ow:split-prompt-source'

/** Marks the next split in this tab as started from the prompt. */
export function markSplitFromPrompt(address: string): void {
	try {
		sessionStorage.setItem(SOURCE_KEY, address)
	} catch {}
}

/** The address whose prompt started this split, consumed once. */
export function takeSplitPromptSource(): string | null {
	try {
		const address = sessionStorage.getItem(SOURCE_KEY)
		sessionStorage.removeItem(SOURCE_KEY)
		return address
	} catch {
		return null
	}
}

function storageKey(kind: 'measured' | 'snoozed' | 'seen', address: string): string {
	return `ow:split-prompt:${kind}:${address}`
}

/** Shown once per wallet: remembered here and on the API (any device). */
export function wasSeenLocally(address: string): boolean {
	try {
		return localStorage.getItem(storageKey('seen', address)) === '1'
	} catch {
		return false
	}
}

export function markSeen(address: string): void {
	try {
		localStorage.setItem(storageKey('seen', address), '1')
	} catch {}
}

/** Whether the API has a 'shown' event for this wallet. Unknown counts as seen,
 * so an API outage never re-shows the prompt. */
export async function wasSeenRemotely(address: string): Promise<boolean> {
	try {
		const res = await fetch(`${API_URL}/site/split-prompt/seen/${encodeURIComponent(address)}`)
		if (!res.ok) return res.status === 404 ? false : true
		const body = (await res.json()) as { seen?: boolean }
		return body.seen !== false
	} catch {
		return true
	}
}

export function isSnoozed(address: string, now = Date.now()): boolean {
	try {
		const until = Number(localStorage.getItem(storageKey('snoozed', address)))
		return Number.isFinite(until) && until > now
	} catch {
		return false
	}
}

export function snooze(address: string, now = Date.now()): void {
	try {
		localStorage.setItem(storageKey('snoozed', address), String(now + SNOOZE_MS))
	} catch {}
}

/** True at most once a day per address, so `measured` stays one row a day. */
export function claimDailyMeasure(address: string, now = new Date()): boolean {
	const day = now.toISOString().slice(0, 10)
	try {
		if (localStorage.getItem(storageKey('measured', address)) === day) return false
		localStorage.setItem(storageKey('measured', address), day)
	} catch {}
	return true
}

const BUILD = /^[0-9a-f]{7,40}$/.test(String(process.env.NEXT_PUBLIC_BUILD_SHA || ''))
	? String(process.env.NEXT_PUBLIC_BUILD_SHA).slice(0, 7)
	: ''

export function recordSplitPrompt(
	kind: SplitPromptKind,
	address: string,
	shape: Partial<UtxoShape> = {},
	extra: { wallet?: string | null; txid?: string | null } = {}
): void {
	try {
		void fetch(`${API_URL}/site/split-prompt`, {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			keepalive: true,
			body: JSON.stringify({
				address,
				kind,
				largest_sats: Math.round(shape.largestSats || 0),
				total_sats: Math.round(shape.totalSats || 0),
				utxo_count: shape.utxoCount || 0,
				wallet: extra.wallet || '',
				build: BUILD,
				txid: extra.txid || ''
			})
		}).catch(() => {})
	} catch {}
}
